// Kierki (Western Hearts, as Microsoft Hearts and the Polish solitaire sites play it against the computer; owner
// 2026-10-26, .ai/multiplayer-plan.md): 4 players, 52 cards, 13 each. Pass 3 cards left, right, across, then none;
// the 2 of clubs leads the first trick; follow suit; no heart and no queen of spades on the first trick unless the
// hand has nothing else; hearts are led only after a heart was played (or the hand has only hearts). Each heart 1
// point, the queen of spades 13; whoever takes all 26 gives 26 to each of the others. At 100 the game ends and the
// lowest total wins.
//
// Pure: no DOM, no React, no Math.random (the caller gives the rng), so the multiplayer server can run the same rules
// for online tables later. Suits here are the REAL suits; only `fileId` maps a card to its picture file, whose names
// swap clubs and spades (`Qc.png` shows the queen of spades, .ai/gotchas.md "Card image files").
// Seats: 0 is the player at the bottom, then clockwise 1 left, 2 top, 3 right; play and "pass left" go clockwise.
import { shuffle } from '../engine/shuffle';

export type HSuit = 'C' | 'D' | 'S' | 'H';
export interface HCard {
  readonly s: HSuit;
  /** 2..14 (11 J, 12 Q, 13 K, 14 A). */
  readonly r: number;
}
export interface Played {
  readonly seat: number;
  readonly card: HCard;
}
export type HeartsPhase = 'pass' | 'play' | 'trick-done' | 'hand-end' | 'over';

export interface HeartsState {
  /** Deals played before this one (0 = the first deal of the game). */
  readonly handNo: number;
  readonly hands: readonly (readonly HCard[])[];
  readonly phase: HeartsPhase;
  /** Seats to the left the 3 cards go: 1 left, 3 right, 2 across, 0 no passing (PASS_OFFSETS by handNo). */
  readonly passOffset: number;
  /** The 3 cards each seat gives (null until that seat chose). */
  readonly passes: readonly (readonly HCard[] | null)[];
  /** The 3 cards each seat got from the passing (shown marked until its first card is played). */
  readonly received: readonly (readonly HCard[])[];
  readonly trick: readonly Played[];
  /** Whose card is next (-1 while passing or when the trick is full). */
  readonly turn: number;
  /** Who took the full trick (phase trick-done), else -1. */
  readonly winner: number;
  readonly heartsBroken: boolean;
  readonly firstTrick: boolean;
  readonly taken: readonly (readonly HCard[])[];
  /** Totals of the finished deals. */
  readonly scores: readonly number[];
  /** Points of each finished deal (after a moon shot). */
  readonly history: readonly (readonly number[])[];
  /** The seat that took all 26 points in the last finished deal, else -1. */
  readonly moon: number;
  /** The shuffle pattern of this deal (52 numbers, engine/shuffle.ts). */
  readonly pattern: readonly number[];
  /** The game ends when a total reaches this (GAME_LIMIT; 50 online, owner 2026-10-29); unset = GAME_LIMIT. */
  readonly limit?: number;
}

export const SEATS = 4;
export const GAME_LIMIT = 100;
export const PASS_OFFSETS = [1, 3, 2, 0] as const;
const SUIT_ORDER: readonly HSuit[] = ['C', 'D', 'H', 'S'];
/** The base deck the shuffle pattern indexes: clubs, diamonds, hearts, spades, each 2..A. */
export const BASE_DECK: readonly HCard[] = SUIT_ORDER.flatMap((s) => Array.from({ length: 13 }, (_, i) => ({ s, r: i + 2 })));

const RANK_NAME: Record<number, string> = { 11: 'J', 12: 'Q', 13: 'K', 14: 'A' };
/** `QS`, `10H`, `2C`: rank + the real suit, for texts, reports and tests. */
export const cardKey = (c: HCard): string => `${RANK_NAME[c.r] ?? c.r}${c.s}`;
/** The card picture's file id (`faceSrc(deck, id)`): the files name clubs `s` and spades `c`. */
export const fileId = (c: HCard): string => `${RANK_NAME[c.r] ?? c.r}${({ C: 's', S: 'c', H: 'h', D: 'd' } as const)[c.s]}`;
export const sameCard = (a: HCard, b: HCard): boolean => a.s === b.s && a.r === b.r;
export const isQueenOfSpades = (c: HCard): boolean => c.s === 'S' && c.r === 12;
export const points = (c: HCard): number => (c.s === 'H' ? 1 : isQueenOfSpades(c) ? 13 : 0);
const has = (cards: readonly HCard[], c: HCard) => cards.some((x) => sameCard(x, c));
const without = (cards: readonly HCard[], out: readonly HCard[]) => cards.filter((c) => !has(out, c));
/** A hand in the order players hold it: clubs, diamonds, spades, hearts (colours alternate), low to high. */
const HAND_ORDER: readonly HSuit[] = ['C', 'D', 'S', 'H'];
export const sortHand = (h: readonly HCard[]): HCard[] => h.slice().sort((a, b) => HAND_ORDER.indexOf(a.s) - HAND_ORDER.indexOf(b.s) || a.r - b.r);

export function passOffsetFor(handNo: number): number {
  return PASS_OFFSETS[handNo % PASS_OFFSETS.length]!;
}

/** A new game: no points yet, the first deal (from `pattern`, else a new one from `rng`). */
export function startGame(pattern: readonly number[] | null, rng: () => number, limit: number = GAME_LIMIT): HeartsState {
  return startHand({ handNo: -1, scores: [0, 0, 0, 0], history: [], limit }, pattern, rng);
}

/** The next deal of a game (handNo + 1); `pattern` replays one (Retry, a report). */
export function startHand(prev: Pick<HeartsState, 'handNo' | 'scores' | 'history' | 'limit'>, pattern: readonly number[] | null, rng: () => number): HeartsState {
  const { order, pattern: used } = shuffle(BASE_DECK.length, pattern, rng);
  const cards = order.map((i) => BASE_DECK[i]!);
  const hands = [0, 1, 2, 3].map((p) => sortHand(cards.slice(p * 13, p * 13 + 13)));
  const handNo = prev.handNo + 1;
  const passOffset = passOffsetFor(handNo);
  const base: HeartsState = {
    handNo, hands, phase: 'pass', passOffset, passes: [null, null, null, null], received: [[], [], [], []], trick: [],
    turn: -1, winner: -1, heartsBroken: false, firstTrick: true, taken: [[], [], [], []], scores: prev.scores,
    history: prev.history, moon: -1, pattern: used, ...(prev.limit !== undefined && prev.limit !== GAME_LIMIT ? { limit: prev.limit } : {}),
  };
  return passOffset === 0 ? beginPlay(base) : base;
}

/** The seat that holds the 2 of clubs. */
export const holderOfTwoOfClubs = (hands: readonly (readonly HCard[])[]): number => hands.findIndex((h) => h.some((c) => c.s === 'C' && c.r === 2));

function beginPlay(s: HeartsState): HeartsState {
  return { ...s, phase: 'play', turn: holderOfTwoOfClubs(s.hands) };
}

/** A seat gives its 3 cards; once all 4 have, the cards change hands and the play begins. Null: not allowed. */
export function submitPass(s: HeartsState, seat: number, cards: readonly HCard[]): HeartsState | null {
  if (s.phase !== 'pass' || s.passes[seat] !== null || cards.length !== 3) return null;
  if (!cards.every((c) => has(s.hands[seat]!, c)) || new Set(cards.map(cardKey)).size !== 3) return null;
  const passes = s.passes.map((p, i) => (i === seat ? cards.slice() : p));
  if (passes.some((p) => p === null)) return { ...s, passes };
  const hands = s.hands.map((h, i) => without(h, passes[i]!));
  const received: HCard[][] = [[], [], [], []];
  for (let from = 0; from < SEATS; from++) received[(from + s.passOffset) % SEATS] = passes[from]!.slice();
  return beginPlay({ ...s, passes, received, hands: hands.map((h, i) => sortHand(h.concat(received[i]!))) });
}

/** The cards `seat` may play now (empty when it is not its turn). */
export function legalCards(s: HeartsState, seat: number): HCard[] {
  if (s.phase !== 'play' || s.turn !== seat) return [];
  const hand = s.hands[seat]!;
  if (!s.trick.length) {
    if (s.firstTrick) return hand.filter((c) => c.s === 'C' && c.r === 2);
    if (!s.heartsBroken) {
      const other = hand.filter((c) => c.s !== 'H');
      if (other.length) return other;
    }
    return hand.slice();
  }
  const led = s.trick[0]!.card.s;
  const follow = hand.filter((c) => c.s === led);
  if (follow.length) return follow;
  if (s.firstTrick) {
    const safe = hand.filter((c) => !points(c));
    if (safe.length) return safe;
  }
  return hand.slice();
}

/** The seat whose card takes the trick: the highest of the suit led. */
export function trickWinner(trick: readonly Played[]): number {
  const led = trick[0]!.card.s;
  let best = trick[0]!;
  for (const p of trick) if (p.card.s === led && p.card.r > best.card.r) best = p;
  return best.seat;
}

/** `seat` plays `card`; null when it may not. A fourth card fills the trick (phase trick-done, `winner` set). */
export function playCard(s: HeartsState, seat: number, card: HCard): HeartsState | null {
  if (!legalCards(s, seat).some((c) => sameCard(c, card))) return null;
  const hands = s.hands.map((h, i) => (i === seat ? without(h, [card]) : h));
  const received = s.received.map((r, i) => (i === seat ? [] : r));
  const trick = [...s.trick, { seat, card }];
  const heartsBroken = s.heartsBroken || card.s === 'H';
  if (trick.length < SEATS) return { ...s, hands, received, trick, heartsBroken, turn: (seat + 1) % SEATS };
  return { ...s, hands, received, trick, heartsBroken, turn: -1, winner: trickWinner(trick), phase: 'trick-done' };
}

/** The full trick goes to its winner, who leads next; after the 13th trick the deal is scored. */
export function collectTrick(s: HeartsState): HeartsState {
  if (s.phase !== 'trick-done') return s;
  const w = s.winner;
  const taken = s.taken.map((t, i) => (i === w ? [...t, ...s.trick.map((p) => p.card)] : t));
  const next: HeartsState = { ...s, taken, trick: [], firstTrick: false, winner: -1, turn: w, phase: 'play' };
  return next.hands.every((h) => h.length === 0) ? scoreHand(next) : next;
}

/** Points of the deal so far, per seat (before a moon shot is applied). */
export const takenPoints = (taken: readonly (readonly HCard[])[]): number[] => taken.map((t) => t.reduce((sum, c) => sum + points(c), 0));

function scoreHand(s: HeartsState): HeartsState {
  const raw = takenPoints(s.taken);
  const moon = raw.indexOf(26);
  const deal = moon >= 0 ? raw.map((_, i) => (i === moon ? 0 : 26)) : raw;
  const scores = s.scores.map((v, i) => v + deal[i]!);
  return { ...s, turn: -1, scores, history: [...s.history, deal], moon, phase: Math.max(...scores) >= (s.limit ?? GAME_LIMIT) ? 'over' : 'hand-end' };
}

/** The seats with the lowest total (more than one on a tie). */
export const leaders = (scores: readonly number[]): number[] => {
  const low = Math.min(...scores);
  return scores.flatMap((v, i) => (v === low ? [i] : []));
};

// ---- the computer players (simple, fair: they see only their own hand and the table) --------------------------

/** The 3 cards a computer player gives: the queen, king and ace of spades, then high hearts, then high cards of its
 *  shortest suits (to empty a suit). */
export function botPass(hand: readonly HCard[]): HCard[] {
  const count = (suit: HSuit) => hand.filter((c) => c.s === suit).length;
  const weight = (c: HCard) => (c.s === 'S' && c.r >= 12 ? 300 + c.r : c.s === 'H' ? 200 + c.r : c.r * 10 - count(c.s));
  return hand.slice().sort((a, b) => weight(b) - weight(a)).slice(0, 3);
}

const highest = (cards: readonly HCard[]) => cards.reduce((m, c) => (c.r > m.r ? c : m));
const lowest = (cards: readonly HCard[]) => cards.reduce((m, c) => (c.r < m.r ? c : m));

/** The card a computer player plays (always one of `legalCards`). */
export function botPlay(s: HeartsState, seat: number): HCard {
  const legal = legalCards(s, seat);
  if (!legal.length) throw new Error(`botPlay: seat ${seat} has no card to play`);
  if (!s.trick.length) {
    const safe = legal.filter((c) => !(c.s === 'S' && c.r >= 12) && c.s !== 'H');
    return lowest(safe.length ? safe : legal);
  }
  const led = s.trick[0]!.card.s;
  if (legal[0]!.s === led) {
    const high = highest(s.trick.filter((p) => p.card.s === led).map((p) => p.card)).r;
    const under = legal.filter((c) => c.r < high);
    if (under.length) return highest(under);
    const noQueen = legal.filter((c) => !isQueenOfSpades(c));
    const pick = noQueen.length ? noQueen : legal;
    const trickPoints = s.trick.some((p) => points(p.card) > 0);
    return s.trick.length === SEATS - 1 && !trickPoints ? highest(pick) : lowest(pick);
  }
  const queen = legal.find(isQueenOfSpades);
  if (queen) return queen;
  const hearts = legal.filter((c) => c.s === 'H');
  if (hearts.length) return highest(hearts);
  const bigSpades = legal.filter((c) => c.s === 'S' && c.r > 12);
  if (bigSpades.length) return highest(bigSpades);
  return highest(legal);
}
